import type { AppLocale, TranslationTree } from './types'

const en: TranslationTree = {
  App: {
    name: 'RFID Operations',
    subtitle: 'Scan & post',
    home: 'Home',
    scanner: 'Scanner',
    rawScan: 'Raw Scan',
    recentScans: 'Recent Scans',
    scannerInfo: 'Scanner Info',
    profile: 'Profile',
    signOut: 'Sign out',
    language: 'Language',
    engine: 'Engine',
  },
  Engine: {
    hubTitle: 'RFID Engine',
    hubSubtitle: 'Birth, tag, and look up LEAP RFID ↔ SKU mappings',
    modules: 'MODULES',
    birthTitle: 'Birth tags',
    birthSubtitle: 'Register LEAP RFID codes with no SKU',
    birthHint: 'Scan tags with your connected scanner, then submit to birth them.',
    birthScanHint: 'Press the trigger on {{scanner}} to scan tags, then Birth.',
    birthAction: 'Birth',
    birthOk: 'Tag birthed',
    birthExists: 'Already birthed',
    birthModeScan: 'Scan',
    birthModeSeries: 'Series',
    birthSeriesHint:
      'Enter the start and end of a LEAP number series (full code or 10-digit serial). Count is inclusive.',
    birthSeriesStart: 'Start',
    birthSeriesEnd: 'End',
    birthSeriesPlaceholder: 'LEAP… or 10-digit serial',
    birthSeriesEmpty: 'Enter start and end to preview count',
    birthSeriesPreview: '{{count}} tags · {{country}} · {{mmyy}} · {{start}}–{{end}}',
    birthSeriesAction: 'Birth series',
    birthSeriesConfirm: 'Birth {{count}} tags from {{start}} to {{end}}?',
    birthSeriesOk: 'Birthed {{created}} of {{expected}} tags',
    birthSeriesSummary:
      'Created {{created}} · already birthed {{existed}} · already tagged {{tagged}}',
    alreadyTagged: 'Already tagged with a SKU',
    alreadyTaggedBirth: '{{count}} tag(s) already have a SKU — birth is only for untagged codes',
    alreadyTaggedTag: '{{count}} tag(s) could not be mapped',
    invalidEncoding: 'Invalid RFID encoding',
    notBirthed: 'Not birthed yet — birth the tag first',
    invalidSku: 'Invalid SKU',
    birthPartial: '{{ok}} birthed, {{failed}} failed',
    tagPartial: '{{ok}} mapped, {{failed}} failed',
    allFailed: '{{failed}} failed',
    allOk: '{{count}} ok',
    birthRawScans: 'Raw scans',
    birthUniqueTags: 'Unique tags',
    birthEmpty: 'No tags yet — press the trigger to scan',
    birthPoolCleared: 'Birth scan pool cleared',
    scannerNotConnected: 'Scanner not connected — tap to connect',
    connectScanner: 'Connect scanner',
    searchScanned: 'Search tags or SKU…',
    searchBirth: 'Search RFID codes…',
    searchTag: 'Search tags or SKU…',
    searchShowing: 'Showing {{shown}} of {{total}}',
    noSearchMatch: 'No tags match this search',
    clear: 'Clear',
    needScan: 'Scan at least one RFID tag',
    tagTitle: 'Tag SKU',
    tagSubtitle: 'Scan tags, then marry each to a SKU (can remap existing mappings)',
    tagAction: 'Map / Remap SKU',
    tagOk: 'SKU mapped',
    tagRemapped: 'Remapped from {{from}} → {{to}}',
    tagSameSku: 'Already mapped to this SKU',
    tagBatchRemapOk: '{{ok}} mapped ({{remapped}} remapped)',
    tagScanHint:
      'Press the trigger on {{scanner}} to scan, then assign a SKU per tag. Re-mapping an already-tagged RFID is allowed.',
    tagMappedCount: 'With SKU',
    tagMapTable: 'Tag → SKU',
    tagPoolCleared: 'Tag SKU pool cleared',
    bulkSku: 'Apply SKU to tags',
    applyEmpty: 'Empty rows',
    applyAll: 'All rows',
    skuPlaceholder: 'Search material / SKU',
    needBulkSku: 'Select a SKU to apply',
    materialsLoadFailed: 'Failed to load material master',
    materialsLoading: 'Loading…',
    materialsCount: '{{count}} materials',
    needSkuPerTag: 'Assign a SKU to each scanned tag',
    incompleteSkuMap: '{{count}} tag(s) still need a SKU',
    skuAppliedEmpty: 'SKU applied to empty rows',
    skuAppliedAll: 'SKU applied to all rows',
    lookupTitle: 'Lookup',
    lookupSubtitle: 'RFID → SKU and SKU → RFIDs',
    lookupAction: 'Look up',
    lookupHintRfid: 'Look up one RFID code to see SKU mapping and birth metadata.',
    lookupHintSku: 'Find every RFID tagged to a SKU.',
    lookupHintBatch: 'Paste multiple RFID codes (space, comma, or newline separated).',
    lookupPlaceholderRfid: 'e.g. 01A250000001',
    lookupPlaceholderSku: 'e.g. MAT-100028',
    lookupPlaceholderBatch: '01A250000001\n01A250000002\n01A250000003',
    lookupEmptyTitle: 'Search the RFID engine',
    lookupEmptyBody:
      'Resolve a single RFID, list every tag on a SKU, or batch-check codes against the birth / tag registry.',
    lookupNoResults: 'No results',
    lookupNoResultsHint: 'Check the code and try again.',
    batchCountHint: '{{count}} unique code(s)',
    unmapped: 'unmapped',
    statusLabel: 'Status',
    country: 'Country',
    monthYear: 'Month / year',
    serial: 'Serial',
    birthedAt: 'Birthed',
    taggedAt: 'Tagged',
    copied: 'Copied',
    copiedAll: 'All RFID codes copied',
    copyAll: 'Copy all',
    tagsLabel: 'Tags',
    filterTags: 'Filter tags…',
    rfidCode: 'RFID code',
    rfidCodes: 'RFID code(s)',
    skuCode: 'SKU code',
    single: 'Single',
    batch: 'Batch',
    batchPairs: 'RFID,SKU per line',
    byRfid: 'By RFID',
    bySku: 'By SKU',
    results: 'Results',
    errors: 'Errors',
    found: 'Found',
    missing: 'Missing',
    needRfid: 'Enter at least one RFID code',
    needRfidSku: 'RFID and SKU are required',
    needBatchPairs: 'Enter RFID,SKU lines',
    needQuery: 'Enter a value to look up',
    noTagsForSku: 'No tagged RFIDs for this SKU',
  },
  auth: {
    productName: 'RFID India',
    heroHeadline: 'Scan with',
    heroHeadlineAccent: 'confidence.',
    heroSubtitle:
      'Connect your Zebra scanner, pick a movement, scan tags against SAP documents, and post warehouse events — the field input layer for RFID Control Tower.',
    brandFeature1Title: 'Scan & capture',
    brandFeature1Desc: 'Pair RFD8500 / RFD40 readers and capture tags against live warehouse documents.',
    brandFeature2Title: 'Post movements',
    brandFeature2Desc: 'Allot, cust-in, dehire, relocation, and transfer — scan in the field, post to SAP.',
    brandFeature3Title: 'IAM-secured access',
    brandFeature3Desc: 'Same LEAP identity and MFA as the POD suite.',
    otpHeroHeadline: 'Two-step',
    otpHeroHeadlineAccent: 'verification.',
  },
  sidebar: {
    profile: 'Profile',
    tools: 'Tools',
    options: 'Options',
    english: 'English',
    arabic: 'العربية',
    scannerConnect: 'Scanner connect',
    scannerConnectSub: 'Pair RFD8500 or RFD40',
    rawScan: 'Raw scan test',
    rawScanSub: 'Scan any tag — no document',
    scannerInfo: 'Scanner info',
    scannerInfoSub: 'Diagnostics & RF parameters',
    recentScans: 'Recent scans',
    recentScansSub: 'SAP documents you opened and scanned',
    scan: 'Scan',
    scanSub: 'Capture tags & post documents',
    logout: 'Log out',
    footerNote: 'LEAP India · RFID Operations',
    signedIn: 'Signed in',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    engine: 'RFID Engine',
    engineSub: 'Birth, tag, lookup (RFID Admin)',
  },
  Scanner: {
    connectTitle: 'Connect scanner',
    connectHint: 'Set your Zebra reader to HID keyboard wedge mode, then connect.',
    connected: 'Connected',
    disconnected: 'Disconnected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    skip: 'Skip for now',
    manualTag: 'Manual EPC entry',
    lastTag: 'Last tag',
  },
  Home: {
    title: 'Movements',
    subtitle: 'Capture warehouse movements — scan tags and post to SAP',
    openConnect: 'Scanner connection',
    tapToConnect: 'Tap to connect a scanner',
    connectedReady: 'ready',
    sectionTitle: 'MOVEMENTS',
  },
  DocumentWizardSuccess: {
    titleFallback: 'Posting complete',
    draftTitle: 'Saved as draft (In Transit)',
    draftDocumentLabel: 'No SAP document — In Transit',
    draftStepSubtitle: 'Step 4 of 4 · Saved as draft',
    postedStepSubtitle: 'Step 4 of 4 · Posted successfully',
    sapDocumentLabel: 'SAP document',
    generatedDocumentLabel: 'Generated document',
    statusLabel: 'Status',
    sapResponseLabel: 'SAP response',
    anotherDocument: 'Another document',
    home: 'Home',
    linesDispatched: 'Lines dispatched',
    tagsScanned: 'Tags scanned',
    vehicle: 'Vehicle',
    dispatchTime: 'Dispatch time',
  },
}

const ar: TranslationTree = {
  App: {
    name: 'RFID Operations',
    subtitle: 'مسح وإرسال',
    home: 'الرئيسية',
    scanner: 'الماسح',
    rawScan: 'مسح خام',
    recentScans: 'عمليات المسح الأخيرة',
    scannerInfo: 'معلومات الماسح',
    profile: 'الملف الشخصي',
    signOut: 'تسجيل الخروج',
    language: 'اللغة',
    engine: 'المحرك',
  },
  Engine: {
    hubTitle: 'محرك RFID',
    hubSubtitle: 'إنشاء الوسوم وربطها والبحث عن ربط RFID ↔ SKU',
    modules: 'الوحدات',
    birthTitle: 'إنشاء الوسوم',
    birthSubtitle: 'تسجيل رموز LEAP RFID بدون SKU',
    birthHint: 'امسح الوسوم بالماسح المتصل ثم أرسلها لإنشائها.',
    birthScanHint: 'اضغط زر التشغيل على {{scanner}} لمسح الوسوم ثم إنشاء.',
    birthAction: 'إنشاء',
    birthOk: 'تم إنشاء الوسم',
    birthExists: 'موجود مسبقاً',
    birthModeScan: 'مسح',
    birthModeSeries: 'سلسلة',
    birthSeriesHint:
      'أدخل بداية ونهاية سلسلة أرقام LEAP (رمز كامل أو تسلسل من 10 أرقام). العدد شامل الطرفين.',
    birthSeriesStart: 'البداية',
    birthSeriesEnd: 'النهاية',
    birthSeriesPlaceholder: 'LEAP… أو تسلسل من 10 أرقام',
    birthSeriesEmpty: 'أدخل البداية والنهاية لمعاينة العدد',
    birthSeriesPreview: '{{count}} وسم · {{country}} · {{mmyy}} · {{start}}–{{end}}',
    birthSeriesAction: 'إنشاء السلسلة',
    birthSeriesConfirm: 'إنشاء {{count}} وسم من {{start}} إلى {{end}}؟',
    birthSeriesOk: 'تم إنشاء {{created}} من {{expected}} وسم',
    birthSeriesSummary:
      'تم الإنشاء {{created}} · موجود مسبقاً {{existed}} · موسوم مسبقاً {{tagged}}',
    alreadyTagged: 'موسوم مسبقاً بـ SKU',
    alreadyTaggedBirth: '{{count}} وسم(وسوم) لديها SKU بالفعل — الإنشاء للوسوم غير الموسومة فقط',
    alreadyTaggedTag: '{{count}} وسم(وسوم) تعذر ربطها',
    invalidEncoding: 'ترميز RFID غير صالح',
    notBirthed: 'لم يتم الإنشاء بعد — أنشئ الوسم أولاً',
    invalidSku: 'SKU غير صالح',
    birthPartial: '{{ok}} تم إنشاؤها، {{failed}} فشلت',
    tagPartial: '{{ok}} تم ربطها، {{failed}} فشلت',
    allFailed: '{{failed}} فشلت',
    allOk: '{{count}} نجحت',
    birthRawScans: 'عمليات المسح',
    birthUniqueTags: 'وسوم فريدة',
    birthEmpty: 'لا توجد وسوم بعد — اضغط زر التشغيل للمسح',
    birthPoolCleared: 'تم مسح مجموعة إنشاء الوسوم',
    scannerNotConnected: 'الماسح غير متصل — اضغط للتوصيل',
    connectScanner: 'توصيل الماسح',
    searchScanned: 'بحث في الوسوم أو SKU…',
    searchBirth: 'ابحث عن رموز RFID…',
    searchTag: 'ابحث في الوسوم أو SKU…',
    searchShowing: 'عرض {{shown}} من {{total}}',
    noSearchMatch: 'لا توجد وسوم تطابق هذا البحث',
    clear: 'مسح',
    needScan: 'امسح وسم RFID واحداً على الأقل',
    tagTitle: 'ربط SKU',
    tagSubtitle: 'امسح الوسوم ثم اربط كل وسم بـ SKU (يمكن إعادة الربط)',
    tagAction: 'ربط / إعادة ربط SKU',
    tagOk: 'تم ربط SKU',
    tagRemapped: 'تمت إعادة الربط من {{from}} → {{to}}',
    tagSameSku: 'مربوط مسبقاً بنفس SKU',
    tagBatchRemapOk: '{{ok}} تم ربطها ({{remapped}} إعادة ربط)',
    tagScanHint:
      'اضغط زر التشغيل على {{scanner}} للمسح ثم عيّن SKU لكل وسم. إعادة ربط وسم موسوم مسبقاً مسموحة.',
    tagMappedCount: 'مع SKU',
    tagMapTable: 'وسم → SKU',
    tagPoolCleared: 'تم مسح مجموعة ربط SKU',
    bulkSku: 'تطبيق SKU على الوسوم',
    applyEmpty: 'الصفوف الفارغة',
    applyAll: 'كل الصفوف',
    skuPlaceholder: 'ابحث عن مادة / SKU',
    needBulkSku: 'اختر SKU للتطبيق',
    materialsLoadFailed: 'فشل تحميل قائمة المواد',
    materialsLoading: 'جاري التحميل…',
    materialsCount: '{{count}} مادة',
    needSkuPerTag: 'عيّن SKU لكل وسم ممسوح',
    incompleteSkuMap: 'لا يزال {{count}} وسم(وسوم) بحاجة إلى SKU',
    skuAppliedEmpty: 'تم تطبيق SKU على الصفوف الفارغة',
    skuAppliedAll: 'تم تطبيق SKU على كل الصفوف',
    lookupTitle: 'بحث',
    lookupSubtitle: 'RFID → SKU و SKU → RFIDs',
    lookupAction: 'بحث',
    lookupHintRfid: 'ابحث عن رمز RFID واحد لعرض ربط SKU وبيانات الإنشاء.',
    lookupHintSku: 'اعثر على كل وسوم RFID المرتبطة بـ SKU.',
    lookupHintBatch: 'الصق عدة رموز RFID (مفصولة بمسافة أو فاصلة أو سطر جديد).',
    lookupPlaceholderRfid: 'مثال: 01A250000001',
    lookupPlaceholderSku: 'مثال: MAT-100028',
    lookupPlaceholderBatch: '01A250000001\n01A250000002\n01A250000003',
    lookupEmptyTitle: 'ابحث في محرك RFID',
    lookupEmptyBody:
      'ابحث عن RFID واحد، أو اعرض كل الوسوم لـ SKU، أو تحقق دفعة من الرموز في سجل الإنشاء / الربط.',
    lookupNoResults: 'لا توجد نتائج',
    lookupNoResultsHint: 'تحقق من الرمز وحاول مرة أخرى.',
    batchCountHint: '{{count}} رمز(رموز) فريد',
    unmapped: 'غير مربوط',
    statusLabel: 'الحالة',
    country: 'الدولة',
    monthYear: 'الشهر / السنة',
    serial: 'التسلسل',
    birthedAt: 'تاريخ الإنشاء',
    taggedAt: 'تاريخ الربط',
    copied: 'تم النسخ',
    copiedAll: 'تم نسخ كل رموز RFID',
    copyAll: 'نسخ الكل',
    tagsLabel: 'الوسوم',
    filterTags: 'تصفية الوسوم…',
    rfidCode: 'رمز RFID',
    rfidCodes: 'رموز RFID',
    skuCode: 'رمز SKU',
    single: 'فردي',
    batch: 'دفعة',
    batchPairs: 'RFID,SKU في كل سطر',
    byRfid: 'حسب RFID',
    bySku: 'حسب SKU',
    results: 'النتائج',
    errors: 'أخطاء',
    found: 'موجود',
    missing: 'غير موجود',
    needRfid: 'أدخل رمز RFID واحداً على الأقل',
    needRfidSku: 'RFID و SKU مطلوبان',
    needBatchPairs: 'أدخل أسطر RFID,SKU',
    needQuery: 'أدخل قيمة للبحث',
    noTagsForSku: 'لا توجد وسوم مربوطة بهذا SKU',
  },
  auth: {
    productName: 'RFID India',
    heroHeadline: 'امسح باستخدام',
    heroHeadlineAccent: 'الثقة.',
    heroSubtitle:
      'وصّل ماسح Zebra، اختر الحركة، امسح الوسوم مقابل مستندات SAP، وأرسل أحداث المستودع — طبقة الإدخال الميداني لـ RFID Control Tower.',
    brandFeature1Title: 'مسح والتقاط',
    brandFeature1Desc: 'اقترن قارئات RFD8500 / RFD40 والتقط الوسوم مقابل مستندات المستودع الحية.',
    brandFeature2Title: 'إرسال الحركات',
    brandFeature2Desc: 'التخصيص واستلام العميل وإلغاء التعيين والنقل والتحويل — امسح في الميدان وأرسل إلى SAP.',
    brandFeature3Title: 'وصول آمن عبر IAM',
    brandFeature3Desc: 'نفس هوية LEAP و MFA كمجموعة POD.',
    otpHeroHeadline: 'التحقق',
    otpHeroHeadlineAccent: 'بثخطوتين.',
  },
  sidebar: {
    profile: 'الملف الشخصي',
    tools: 'الأدوات',
    options: 'الخيارات',
    english: 'English',
    arabic: 'العربية',
    scannerConnect: 'توصيل الماسح',
    scannerConnectSub: 'اقتران RFD8500 أو RFD40',
    rawScan: 'اختبار المسح الخام',
    rawScanSub: 'امسح أي علامة — بدون مستند',
    scannerInfo: 'معلومات الماسح',
    scannerInfoSub: 'التشخيص ومعاملات RF',
    recentScans: 'عمليات المسح الأخيرة',
    recentScansSub: 'مستندات SAP التي فتحتها ومسحتها',
    scan: 'مسح',
    scanSub: 'التقاط الوسوم وإرسال المستندات',
    logout: 'تسجيل الخروج',
    footerNote: 'LEAP India · RFID Operations',
    signedIn: 'مسجّل الدخول',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    engine: 'محرك RFID',
    engineSub: 'إنشاء وربط وبحث (RFID Admin)',
  },
  Scanner: {
    connectTitle: 'توصيل الماسح',
    connectHint: 'اضبط قارئ Zebra على وضع لوحة المفاتيح HID ثم اتصل.',
    connected: 'متصل',
    disconnected: 'غير متصل',
    connect: 'اتصال',
    disconnect: 'قطع الاتصال',
    skip: 'تخطي الآن',
    manualTag: 'إدخال EPC يدوي',
    lastTag: 'آخر علامة',
  },
  Home: {
    title: 'الحركات',
    subtitle: 'التقاط حركات المستودع — امسح الوسوم وأرسل إلى SAP',
    openConnect: 'اتصال الماسح',
    tapToConnect: 'اضغط لتوصيل ماسح',
    connectedReady: 'جاهز',
    sectionTitle: 'الحركات',
  },
  DocumentWizardSuccess: {
    titleFallback: 'اكتمل الإرسال',
    draftTitle: 'تم الحفظ كمسودة (قيد النقل)',
    draftDocumentLabel: 'لا يوجد مستند SAP — قيد النقل',
    draftStepSubtitle: 'الخطوة 4 من 4 · تم الحفظ كمسودة',
    postedStepSubtitle: 'الخطوة 4 من 4 · تم الإرسال بنجاح',
    sapDocumentLabel: 'مستند SAP',
    generatedDocumentLabel: 'المستند المُنشأ',
    statusLabel: 'الحالة',
    sapResponseLabel: 'استجابة SAP',
    anotherDocument: 'مستند آخر',
    home: 'الرئيسية',
    linesDispatched: 'البنود المُرسلة',
    tagsScanned: 'الوسوم الممسوحة',
    vehicle: 'المركبة',
    dispatchTime: 'وقت الإرسال',
  },
}

export const FALLBACK: Record<AppLocale, TranslationTree> = { en, ar }

export function getTranslations(locale: AppLocale): TranslationTree {
  return FALLBACK[locale] ?? FALLBACK.en
}

export function isRtlLocale(locale: AppLocale): boolean {
  return locale === 'ar'
}
